import {
    getBuilderDefaultContactFieldLabel,
    getContactInfoTranslations,
    getDefaultContactFieldLabel,
} from '@common/i18n/contactInfo';
import {
    CONTACT_FIELD_LABEL_KEYS,
    type ContactFieldLabels,
    type TemplateFormLanguage,
    toSparseContactFieldLabels,
} from '@common/types/templateForms';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';

const CUSTOM_LABEL_MAX_LENGTH = 200;

interface Props {
    open: boolean;
    language: TemplateFormLanguage;
    fieldLabels?: ContactFieldLabels;
    onClose: () => void;
    onSave: (fieldLabels: ContactFieldLabels | undefined) => void | Promise<void>;
}

export default function RenameContactFieldLabelsDialog({
    open,
    language,
    fieldLabels,
    onClose,
    onSave,
}: Props) {
    const t = getContactInfoTranslations(language);
    const [draft, setDraft] = useState<ContactFieldLabels>({});
    const [isSaving, setIsSaving] = useState(false);

    // Start from the saved labels each time the dialog opens
    const [wasOpen, setWasOpen] = useState(false);
    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) {
            setDraft(fieldLabels ?? {});
            setIsSaving(false);
        }
    }

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(toSparseContactFieldLabels(draft));
            onClose();
        } catch {
            // Parent handler reports the error; keep the dialog open so labels aren't lost.
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>Rename contact field labels</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Add a custom label to override the default, for example &quot;First Name
                    (名)&quot;. Leave a custom label blank to keep the default. Only filled-in
                    labels are saved.
                </Typography>
                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '50%' }}>Default Label</TableCell>
                                <TableCell>Custom Label</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {CONTACT_FIELD_LABEL_KEYS.map((field) => {
                                const defaultLabel = getDefaultContactFieldLabel(field, t);
                                return (
                                    <TableRow key={field}>
                                        <TableCell>
                                            {getBuilderDefaultContactFieldLabel(field, t)}
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={draft[field] ?? ''}
                                                placeholder={defaultLabel}
                                                inputProps={{ maxLength: CUSTOM_LABEL_MAX_LENGTH }}
                                                onChange={(event) =>
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        [field]: event.target.value,
                                                    }))
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
